import { CONSTANTS, CANVAS_ELEMENT_SNAPPING, PREVIEW_BASE_HEIGHT } from '../PreviewDisplayConstants';

// Every element on the canvas is authored against a frame PREVIEW_BASE_HEIGHT units tall, and a stored
// position is written in those units. Everything in this file stays in them: the frame, the elements
// already on the canvas and the position being dragged are then all in one space, and no conversion
// sits between a snap target and the number that gets stored.

// Which line of the dragged element caught the target. An element offers three lines per axis, and any
// of them can land on any target, which is what lets two elements be butted edge to edge as well as
// lined up down their middles.
const MOVING_LINE_OFFSETS = [-1, 0, 1];

// The frame in base units. Its middle is the origin, because that is where a stored position is
// measured from.
export function measureFrame() {
    const multiplier = CONSTANTS.DEFAULT_HEIGHT / PREVIEW_BASE_HEIGHT;

    return {
        multiplier: multiplier,
        halfWidth: CONSTANTS.DEFAULT_WIDTH * 0.5 / multiplier,
        halfHeight: PREVIEW_BASE_HEIGHT * 0.5
    };
}

// A point on the renderer's pixel grid, in base units measured from the middle of the frame, which is
// where a stored position is measured from.
export function toBaseUnits(rendererPoint) {
    const { multiplier } = measureFrame();

    return {
        x: (rendererPoint.x - CONSTANTS.DEFAULT_WIDTH * 0.5) / multiplier,
        y: (rendererPoint.y - CONSTANTS.DEFAULT_HEIGHT * 0.5) / multiplier
    };
}

// A rectangle read off the display tree, in base units measured from the middle of the frame. This is
// the one place renderer pixels turn into the space snapping and the position limits work in, so every
// kind of element is measured the same way whatever it is made of.
export function toBaseUnitBox(bounds) {
    if (!bounds || bounds.width <= 0 || bounds.height <= 0)
        return null;

    const { multiplier } = measureFrame();

    return {
        centerX: (bounds.x + bounds.width * 0.5 - CONSTANTS.DEFAULT_WIDTH * 0.5) / multiplier,
        centerY: (bounds.y + bounds.height * 0.5 - CONSTANTS.DEFAULT_HEIGHT * 0.5) / multiplier,
        halfWidth: bounds.width * 0.5 / multiplier,
        halfHeight: bounds.height * 0.5 / multiplier
    };
}

// The same conversion the other way round, for an element whose box is worked out from its settings
// rather than read off the display tree: the rectangle a base-unit box covers on the renderer's grid.
export function toRendererBounds(box) {
    if (!box)
        return null;

    const { multiplier } = measureFrame();

    return {
        x: CONSTANTS.DEFAULT_WIDTH * 0.5 + (box.centerX - box.halfWidth) * multiplier,
        y: CONSTANTS.DEFAULT_HEIGHT * 0.5 + (box.centerY - box.halfHeight) * multiplier,
        width: box.halfWidth * 2 * multiplier,
        height: box.halfHeight * 2 * multiplier
    };
}

// Every line a dragged element can catch on, gathered once at the start of a drag: none of them moves
// while the drag runs, so recomputing them per frame would only cost bounds reads.
//
// Three sources, exactly as the client listed them: the middle of the frame, a safe zone set the same
// distance in from every edge, and the edges and middles of every other element on the canvas.
export function collectSnapTargets(draggedTarget, targets) {
    const frame = measureFrame();
    const margin = CANVAS_ELEMENT_SNAPPING.SAFE_ZONE_MARGIN;
    const safeHalfWidth = frame.halfWidth - margin;
    const safeHalfHeight = frame.halfHeight - margin;

    const snapTargets = { x: [0], y: [0] };

    // A frame narrower than two margins has no room for a safe zone, and its edges would land on the
    // wrong side of the middle.
    if (safeHalfWidth > 0)
        snapTargets.x.push(-safeHalfWidth, safeHalfWidth);

    if (safeHalfHeight > 0)
        snapTargets.y.push(-safeHalfHeight, safeHalfHeight);

    targets.forEach(target => {
        if (target.id === draggedTarget.id)
            return;

        // Which lines an element offers is its own business, and one with nothing on screen to align by
        // offers none.
        const lines = target.measureSnapLines();

        if (!lines)
            return;

        snapTargets.x.push(...lines.x);
        snapTargets.y.push(...lines.y);
    });

    return snapTargets;
}

// Pulls a box onto the nearest alignment line it is close enough to. The two axes are decided
// separately, so an element can sit on the vertical middle of the frame while its top edge is still
// free, and moving on past the threshold lets go of the line without any further ceremony.
//
// Returns the box middle to use and the lines that were caught, which is what the guides draw.
export function snapBoxCenter(boxCenter, halfSize, targets, snapDistance) {
    const x = snapAxis(boxCenter.x, halfSize.halfWidth, targets.x, snapDistance);
    const y = snapAxis(boxCenter.y, halfSize.halfHeight, targets.y, snapDistance);

    const guides = [];

    if (x.line !== null)
        guides.push({ axis: 'x', line: x.line });

    if (y.line !== null)
        guides.push({ axis: 'y', line: y.line });

    return { center: { x: x.value, y: y.value }, guides: guides };
}

// The nearest target wins. Distances are compared before anything is moved, so an element between two
// lines catches the one it is actually closer to rather than the first one checked.
function snapAxis(center, half, targetLines, snapDistance) {
    let nearestShift = null;
    let nearestLine = null;
    let nearestDistance = snapDistance;

    targetLines.forEach(target => {
        MOVING_LINE_OFFSETS.forEach(offset => {
            const distance = Math.abs(target - (center + offset * half));

            if (distance > nearestDistance)
                return;

            nearestDistance = distance;
            nearestShift = target - (center + offset * half);
            nearestLine = target;
        });
    });

    return nearestShift === null
        ? { value: center, line: null }
        : { value: center + nearestShift, line: nearestLine };
}

// Holds the element's stored position inside the frame that is actually on screen, which is also the
// range the X and Y Position sliders offer. Both gestures need it: a resize moves the picture as well
// as scaling it, because the corner opposite the dragged one stays put. Positions are whole slider
// steps.
//
// A limit that is somehow not a number would turn the position into NaN and lose the element
// altogether, which is a worse outcome than a gesture that runs unclamped.
//
// `boxOffset` is how far the element's box sits from its stored position. A picture and the visualizer
// are drawn around their position and pass none, but a line of text hangs off its position to one side
// when it is anchored left or right. Limiting the position alone would then let a left-anchored line be
// carried until its left edge reached the right edge of the frame, with every bit of it off the canvas
// and nothing left to grab. So the box middle is held on the frame as well, which leaves at least half
// of the line on screen exactly as the limit already does for a picture.
export function clampToPositionRange(position, { maxXPosition, maxYPosition }, boxOffset = { x: 0, y: 0 }) {
    return {
        x: clampToLimit(position.x, maxXPosition, boxOffset.x),
        y: clampToLimit(position.y, maxYPosition, boxOffset.y)
    };
}

function clampToLimit(value, limit, boxOffset = 0) {
    if (!Number.isFinite(limit))
        return withoutNegativeZero(Math.round(value));

    const { lowest, highest } = calculateAllowedRange(limit, Number.isFinite(boxOffset) ? boxOffset : 0);

    return withoutNegativeZero(Math.round(Math.min(Math.max(value, lowest), highest)));
}

// Both ranges at once: the position has to stay a value the slider offers, and the box middle has to
// stay on the frame. With no offset the two are the same range.
function calculateAllowedRange(limit, boxOffset) {
    const lowest = Math.max(-limit, -limit - boxOffset);
    const highest = Math.min(limit, limit - boxOffset);

    // A line wider than the whole frame cannot satisfy both. It is then held where its middle comes
    // closest to the middle of the frame, which is where most of it is on screen.
    if (lowest > highest) {
        const closestToMiddle = Math.min(Math.max(-boxOffset, -limit), limit);
        return { lowest: closestToMiddle, highest: closestToMiddle };
    }

    return { lowest, highest };
}

function withoutNegativeZero(rounded) {
    // Rounding anything in [-0.5, 0) gives negative zero, which reads as "0" everywhere a user can see
    // it but is not equal to it, so a position that landed dead centre from the left compares as a
    // different position from one that landed there from the right.
    return rounded === 0 ? 0 : rounded;
}

// The snap threshold in base units. It is written in screen pixels, so it has to be divided back
// through both the renderer's own grid and the CSS scaling of the painted frame; without the second
// one a snap would feel twice as strong on a narrow window as on a wide one.
export function calculateSnapDistance(paintedFrameScale) {
    const { multiplier } = measureFrame();
    const screenPixelsPerBaseUnit = multiplier * paintedFrameScale;

    if (!(screenPixelsPerBaseUnit > 0))
        return 0;

    return CANVAS_ELEMENT_SNAPPING.DISTANCE_IN_SCREEN_PIXELS / screenPixelsPerBaseUnit;
}
